import { ref } from 'vue';
import { getTelnyxToken } from '@/api/in_app_phone_calls';
import { webRTCCallSingletonService } from '@/lib/webrtc';
import { toast } from 'vue-sonner';

type CallState = 'idle' | 'connecting' | 'ringing' | 'active' | 'ended' | 'error';

export function useInAppPhoneCalls() {
  const callState = ref<CallState>('idle');
  const isInitialized = ref(false);
  const callStartTime = ref<Date | null>(null);
  const callDurationSeconds = ref<number>(0);

  function reset(): void {
    callState.value = 'idle';
    callStartTime.value = null;
    callDurationSeconds.value = 0;
  }

  function endCall(): void {
    callState.value = 'ended';
    if (callStartTime.value) {
      const endTime = new Date();
      callDurationSeconds.value = Math.floor((endTime.getTime() - callStartTime.value.getTime()) / 1000);
    }
  }

  async function initializeWebRTC(): Promise<void> {
    if (isInitialized.value) return;

    try {
      console.log('🔑 Fetching Telnyx token...');
      const token = await getTelnyxToken();

      if (!token) {
        throw new Error('No token received from server');
      }

      console.log('🔑 Initializing WebRTC with token:', token.substring(0, 20) + '...');

      await webRTCCallSingletonService.initialize({
        credentials: { token },
        debug: true,
      });

      console.log('✅ WebRTC initialized successfully');
      isInitialized.value = true;
    } catch (error) {
      console.error('❌ WebRTC initialization failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to initialize calling service', {
        description: errorMessage,
      });
      throw error;
    }
  }

  async function makeCall(
    destinationNumber: string,
    callerNumber: string,
    contactId?: number,
    userId?: number
  ): Promise<void> {
    try {
      callState.value = 'connecting';
      callStartTime.value = new Date();

      await initializeWebRTC();

      console.log('🚀 Making WebRTC call with params:', {
        destinationNumber,
        callerNumber,
        contactId,
        userId,
      });

      await webRTCCallSingletonService.makeCall({
        destinationNumber,
        callerNumber,
        contactId,
        userId,
      });

      console.log('✅ WebRTC call initiated, setting state to ringing');
      callState.value = 'ringing';

      webRTCCallSingletonService.on('callConnected', () => {
        console.log('📞 Call connected successfully');
        callState.value = 'active';
        toast.success('Call connected');
      });

      webRTCCallSingletonService.on('callHungUp', () => {
        console.log('📞 Call hung up');
        endCall();
      });
    } catch (error) {
      console.error('❌ WebRTC call failed:', error);
      callState.value = 'error';
      toast.error('Failed to make call', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  function hangUp(): void {
    webRTCCallSingletonService.hangUp();
    endCall();
  }

  return {
    callState,
    callDurationSeconds,
    makeCall,
    hangUp,
    reset,
  };
}
